<template>
    <form autocomplete="off">
      <div class="fundingNeedsAmount">
        <p class="fundingNeedsTitle">{{ t('additionalRequestedAmount.label') }}</p>
        <p class="fundingNeedsSubtitle">{{ t('additionalRequestedAmount.subtitle') }}</p>
        <input-inline>
          <input-money
            id="additional-requested-amount"
            name="additional-requested-amount"
            class="fundingNeedsInput"
            :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
            :maxlength="FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH"
            v-model="additionalRequestedAmount.value"
            :placeholder="t('additionalRequestedAmount.placeholder')"
          >
            <template #prefix>{{ $t('currency.dollar') }}</template>
          </input-money>
        </input-inline>
      </div>

      <div class="fundingNeedsAmount">
        <p class="fundingNeedsTitle">{{ t('outstandingBalance.label') }}</p>
        <p class="fundingNeedsSubtitle">{{ t('outstandingBalance.subtitle') }}</p>
        <input-inline>
          <input-money
            id="outstanding-balance"
            name="outstanding-balance"
            :disabled="true"
            class="fundingNeedsInput"
            :value="renewalPayOffAmount"
          >
            <template #prefix>{{ $t('currency.dollar') }}</template>
          </input-money>
        </input-inline>
      </div>

      <div class="fundingNeedsAmount">
        <p class="fundingNeedsTitle">{{ t('totalLoanAmount.label') }}</p>
        <p class="fundingNeedsSubtitle">{{ t('totalLoanAmount.subtitle') }}</p>
        <input-inline>
          <input-money
            id="total-loan-amount"
            name="total-loan-amount"
            :disabled="true"
            class="fundingNeedsInput"
            :value="totalLoanAmount"
          >
            <template #prefix>{{ $t('currency.dollar') }}</template>
          </input-money>
        </input-inline>
        <p v-if="totalLoanAmountError" class="fundingNeedsError">
          {{ totalLoanAmountError }}
        </p>
      </div>

    </form>
</template>

<script>
import * as funds from '@/modules/funds';
import { mapGetters, mapActions } from 'vuex';
import {
  FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';
import { LOAN_AMOUNT_MIN, LOAN_AMOUNT_MAX } from '@/modules/validation-service/constants';

import InputInline from '@/components/InputInline';
import applicationMixin from '@/mixins/application';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import buildTranslators from '@/utils/build-translators';

export default {
  name: 'funding-needs',

  components: {
    InputInline,
  },

  data() {
    const self = this;
    return {
      FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH,
      additionalRequestedAmount: {
        get value() {
          return self.uiApplicationState?.additionalRequestedAmount;
        },
        set value(value) {
          if (value === undefined) {
            return;
          }
          self.updateUIStateApplication({
            ...self.uiApplicationState,
            additionalRequestedAmount: value.toString(),
          });
        },
      },
    };
  },

  computed: {
    ...mapGetters('application', { uiApplicationState: 'getUiStateApplication' }),
    ...mapGetters({
      outstandingBalanceBAIPF: 'offer/getOutstandingBalance',
    }),
    renewalPayOffAmount() {
      return Number(this.outstandingBalanceBAIPF || 0);
    },
    totalLoanAmount() {
      const desired = Number(this.uiApplicationState?.additionalRequestedAmount) || 0;
      return Math.round(this.renewalPayOffAmount + desired);
    },
    totalLoanAmountError() {
      if (this.totalLoanAmount > LOAN_AMOUNT_MAX) {
        return this.t('totalLoanAmount.errorMax');
      }
      if (this.totalLoanAmount < LOAN_AMOUNT_MIN && this.totalLoanAmount > 0) {
        return this.t('totalLoanAmount.errorMin');
      }
      return null;
    },
  },

  mixins: [applicationMixin],

  watch: {
    totalLoanAmount: {
      immediate: true,
      handler(newVal) {
        if (newVal !== undefined) {
          this.updateUIStateApplication({
            ...this.uiApplicationState,
            requestedAmount: Math.round(newVal).toString(),
          });
        }
      },
    },
  },

  methods: {

    t(key, options) {
      return buildTranslators('fundingNeeds').t(key, options);
    },
    ...mapActions('application', ['updateUIStateApplication']),
  },
};
</script>

<style lang="sass" scoped>
.fundingNeeds
  h1
    margin-bottom: $glass-spacing-3
  .nextButton
    margin-top: $glass-spacing-4

.fundingNeedsTitle
  +font-size(16)
  margin-bottom: $glass-spacing-1
  color: $color-system-neutral-gray-6

.fundingNeedsSubtitle
  +font-size(13.33)
  margin: $glass-spacing-1 0
  color: $color-system-neutral-gray-5

.fundingNeedsInput
  margin: 0

.fundingNeedsError
  +font-size(13.33)
  margin-top: $glass-spacing-1
  color: $color-system-validation-error

.fundingNeedsAmount
  margin-top: $glass-spacing-4

.fundingNeedsPurpose
  margin-top: $glass-spacing-4

#desired-loan-amount
  margin: $glass-spacing-3 0 $glass-spacing-3

.fundingSelectionCaption
  margin-top: $glass-spacing-1
  text-transform: none
  font-weight: normal
  font-style: italic
  line-height: 1.33
  color: $color-system-validation-error
  +font-size(13.33)

.fundingNeedsOtherPurposes
  margin-top: $glass-spacing-4
  display: grid;
  grid-template-columns: repeat(2, 1fr);

.fundingOtherReasonsTitle
  font-weight: bold

.fundingOtherReasons
  margin: $glass-spacing-105 0

.fundingOtherReasonsLabel
  +font-size(16)
</style>
